import React from 'react';
import { Trash2, FileText, X, AlertTriangle, ChevronDown } from 'lucide-react';
import '../styles/SelectedEquipment.css';

// Usage values match the ones the calculator already understands; only the
// labels change, to read the way the client's report does ("5 h / jour").
const USAGE_OPTIONS = [
  { value: '0.08', label: '5 min / jour' },
  { value: '0.16', label: '10 min / jour' },
  { value: '0.25', label: '15 min / jour' },
  { value: '0.33', label: '20 min / jour' },
  { value: '0.5', label: '30 min / jour' },
  { value: '0.75', label: '45 min / jour' },
  ...Array.from({ length: 24 }, (_, i) => {
    const hours = i + 1;
    return {
      value: String(hours),
      label: hours === 24 ? '24 h / 24 h' : `${hours} h / jour`
    };
  })
];

const QUANTITY_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 20, 30, 40, 50];

const FALLBACK_ICON = 'https://via.placeholder.com/30/3D5A73/FFFFFF?text=⚡';

// The two numbered sections of the client's 12KWH report. Categories not
// listed as "base" (continuous use) fall into the high-power section.
const BASE_CATEGORY_IDS = [0, 1, 2, 3, 7]; // Éclairage, Téléphonie & PC, Son & Image, Électroménager, Sécurité
const SECTIONS = [
  {
    title: 'Équipements de Base & Multimédia (Usage Continu)',
    matches: id => BASE_CATEGORY_IDS.includes(id)
  },
  {
    title: 'Équipements Électroménagers & Fortes Puissances (Ponctuels)',
    matches: id => !BASE_CATEGORY_IDS.includes(id)
  }
];

const formatKwh = (wh, digits = { maximumFractionDigits: 1 }) =>
  (wh / 1000).toLocaleString(undefined, digits);

const hoursLabel = (hours) => (hours === 24 ? '24 h / 24 h' : `${hours} h / jour`);

const SelectedEquipmentTable = ({
  productName,
  selectedEquipments,
  consumption,
  production,
  getEquipmentConsumption,
  updateEquipment,
  updateEquipmentOption,
  removeEquipment,
  onReset,
  onQuoteClick
}) => {
  const isEmpty = selectedEquipments.length === 0;
  const isOverBudget = production > 0 && consumption > production;
  const isNearLimit = !isOverBudget && production > 0 && consumption > production * 0.8;
  const marginState = isOverBudget ? 'over' : isNearLimit ? 'near' : 'ok';

  // Stable sort keeps lines of a category in the order they were added.
  const sections = SECTIONS
    .map(section => ({
      ...section,
      items: selectedEquipments
        .filter(equip => section.matches(equip.id))
        .sort((a, b) => a.id - b.id)
    }))
    .filter(section => section.items.length > 0);

  return (
    <div className="fiche">
      <div className="fiche__head">
        <h5 className="fiche__title">Fiche &amp; Bilan Énergétique</h5>
        {productName && <div className="fiche__sub">{productName}</div>}
      </div>

      <div className="fiche__kpis">
        <div className="fiche__kpi">
          <span className="fiche__kpi-label">Production quotidienne moyenne</span>
          <span className="fiche__kpi-value fiche__kpi-value--sky">
            {formatKwh(production, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kWh
          </span>
        </div>
        <div className="fiche__kpi">
          <span className="fiche__kpi-label">Consommation estimée</span>
          <span className={`fiche__kpi-value ${isOverBudget ? 'fiche__kpi-value--over' : 'fiche__kpi-value--green'}`}>
            {formatKwh(consumption)} kWh
          </span>
        </div>
        <div
          className="fiche__kpi"
          title="Gardez une marge d'environ 20 % entre votre consommation et la production du pack"
        >
          <span className="fiche__kpi-label">Marge de sécurité</span>
          <span className={`fiche__kpi-value fiche__kpi-value--margin-${marginState}`}>(±20%)</span>
        </div>
      </div>

      {isOverBudget && (
        <div className="fiche__alert" role="status">
          <AlertTriangle size={16} aria-hidden="true" />
          <span>
            Votre consommation dépasse la production de ce pack. Choisissez un pack plus puissant
            ou réduisez votre consommation.
          </span>
        </div>
      )}

      {isEmpty ? (
        <div className="fiche__empty">
          <strong>Aucun équipement sélectionné</strong>
          Choisissez vos équipements dans la liste pour établir votre bilan énergétique.
        </div>
      ) : (
        <div className="fiche__list">
          {sections.map((section, sectionIndex) => (
            <section key={section.title} className="fiche__section">
              <h6 className="fiche__section-title">
                {sectionIndex + 1}. {section.title}
              </h6>

              <table className="fiche__table">
                <colgroup>
                  <col />
                  <col className="fiche__col-qty" />
                  <col className="fiche__col-watt" />
                  <col className="fiche__col-usage" />
                  <col className="fiche__col-total" />
                  <col className="fiche__col-remove" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Équipement</th>
                    <th className="is-center" title="Quantité">Qté</th>
                    <th className="is-center">Puissance</th>
                    <th className="is-center">Utilisation</th>
                    <th className="is-right">Total</th>
                    <th><span className="visually-hidden">Retirer</span></th>
                  </tr>
                </thead>
                <tbody>
                  {section.items.map(equip => {
                    const watt = equip.selectedOption?.watt ?? equip.puissance ?? 0;
                    const label = (equip.selectedOption?.name || equip.name).trim();
                    const hours = equip.selectedOption?.utilization;
                    // Électroménager options already hold the daily total, so power
                    // is shown as the average over its hours of use, like the report.
                    const totalGiven = equip.id === 3 || equip.totalGiven;

                    return (
                      <tr key={equip.instanceId} className="fiche__row">
                        <td className="fiche__cell-equip" data-label="Équipement">
                          <div className="fiche__equip">
                            <img
                              src={equip.selectedOption?.image || equip.image}
                              alt=""
                              className="fiche__equip-icon"
                              onError={(e) => { e.target.src = FALLBACK_ICON; }}
                            />
                            <div className="fiche__equip-main">
                              {equip.options && equip.options.length > 0 ? (
                                <label className="fiche__picker">
                                  <span className="fiche__picker-text">{label}</span>
                                  <ChevronDown size={14} className="fiche__picker-caret" aria-hidden="true" />
                                  <select
                                    aria-label={`Option pour ${equip.name}`}
                                    value={equip.selectedOption?.id ?? ''}
                                    onChange={(e) => {
                                      const selected = equip.options.find(
                                        opt => opt.id === parseInt(e.target.value, 10)
                                      );
                                      if (selected) updateEquipmentOption(equip.instanceId, selected);
                                    }}
                                  >
                                    <option value="">Sélectionnez une option</option>
                                    {equip.options.map(option => (
                                      <option key={option.id} value={option.id}>
                                        {option.name.trim()}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                              ) : (
                                <span className="fiche__name">{equip.name}</span>
                              )}

                              {equip.showWarning && (
                                <div className="fiche__warning">
                                  <AlertTriangle size={12} />
                                  Batterie au Lithium nécessaire
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="is-center fiche__cell-qty" data-label="Quantité">
                          {equip.multipleQuantity ? (
                            <select
                              className="fiche__select fiche__select--qty"
                              aria-label={`Quantité pour ${label}`}
                              value={equip.quantity || 1}
                              onChange={(e) => updateEquipment(equip.instanceId, 'quantity', e.target.value)}
                            >
                              {QUANTITY_OPTIONS.map(q => (
                                <option key={q} value={q}>{q}</option>
                              ))}
                            </select>
                          ) : (
                            equip.quantity || 1
                          )}
                        </td>

                        <td className="is-center fiche__cell-watt" data-label="Puissance">
                          {totalGiven && hours
                            ? `~${Math.round(watt / hours).toLocaleString()} W (moy)`
                            : `${watt.toLocaleString()} W`}
                        </td>

                        <td className="is-center fiche__cell-usage" data-label="Utilisation">
                          {totalGiven ? (
                            hours ? hoursLabel(hours) : '—'
                          ) : (
                            <select
                              className="fiche__select"
                              aria-label={`Utilisation pour ${label}`}
                              value={equip.usage || 1}
                              onChange={(e) => updateEquipment(equip.instanceId, 'usage', e.target.value)}
                            >
                              {USAGE_OPTIONS.map(u => (
                                <option key={u.value} value={u.value}>{u.label}</option>
                              ))}
                            </select>
                          )}
                        </td>

                        <td className="is-right fiche__cell-total" data-label="Total">
                          {getEquipmentConsumption(equip).toLocaleString()} Wh
                        </td>

                        <td className="is-right fiche__cell-remove">
                          <button
                            type="button"
                            className="fiche__remove"
                            aria-label={`Retirer ${label}`}
                            title="Retirer"
                            onClick={() => removeEquipment(equip.instanceId)}
                          >
                            <X size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      )}

      <div className={`fiche__total ${isOverBudget ? 'fiche__total--over' : ''}`}>
        <span className="fiche__total-label">Total consommation quotidienne estimée :</span>
        <span className="fiche__total-value">
          {consumption.toLocaleString()} Wh (~{formatKwh(consumption)}kWh/j)
        </span>
      </div>

      <div className="fiche__actions">
        <button type="button" className="fiche__btn" onClick={onReset} disabled={isEmpty}>
          <Trash2 size={16} />
          Réinitialiser
        </button>
        <button type="button" className="fiche__btn fiche__btn--primary" onClick={onQuoteClick}>
          <FileText size={16} />
          Demander un devis
        </button>
      </div>
    </div>
  );
};

export default SelectedEquipmentTable;
